import { importLibrary, setOptions } from '@googlemaps/js-api-loader'
import { usePortalDropdownPosition } from '@hooks'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { config } from '../../config'
import {
    AutocompleteContainer,
    AutocompleteDropdown,
    AutocompleteItem,
    ClearButton,
    CustomLocationItem,
    EmptyAutocompleteItem,
    Input,
    MainText,
    SecondaryText,
} from './LocationAutocomplete.styles'

export interface PlaceSuggestion {
	placeId: string
	primaryText: string
	secondaryText: string
	fullText: string
	types: string[]
}

interface LocationAutocompleteProps {
	value: string
	onChange: (value: string) => void
	onPlaceSelect: (place: PlaceSuggestion) => void
	placeholder?: string
	id?: string
	autoFocus?: boolean
	/** Accessible name when no visible `<label>` points at the input. */
	ariaLabel?: string
	/**
	 * Opts the field into free text. Given it, the list always ends with a
	 * "use it as typed" row carrying the trimmed query, and Enter picks that
	 * row — so a place Google doesn't know is reachable whenever something is
	 * typed, not only when the search comes back empty.
	 */
	onFreeTextSelect?: (text: string) => void
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
	value,
	onChange,
	onPlaceSelect,
	placeholder = 'Search for a location...',
	id,
	autoFocus = false,
	ariaLabel,
	onFreeTextSelect,
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null)
	const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken | null>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)
	const dropdownCoords = usePortalDropdownPosition(containerRef, isOpen)

	const query = value.trim()
	const allowFreeText = onFreeTextSelect !== undefined
	/** The trailing "use it as typed" row is offered whenever there is a query. */
	const showCustomRow = allowFreeText && query !== ''
	const isSearching = isLoading && suggestions.length === 0 && query !== ''
	/** Google came back with nothing — said in the list, above the custom row. */
	const showNoMatchesNote = showCustomRow && !isLoading && suggestions.length === 0

	// Initialize Google Maps API
	useEffect(() => {
		const apiKey = config.googleMapsApiKey
		if (!apiKey) {
			console.error('Google Maps API key is not configured')
			return
		}

		// Use the new functional API
		setOptions({ key: apiKey })

		importLibrary('places')
			.then(() => {
				// After load completes, google is available globally
				const service = new google.maps.places.AutocompleteService()
				const token = new google.maps.places.AutocompleteSessionToken()
				setAutocompleteService(service)
				setSessionToken(token)
			})
			.catch((error: Error) => {
				console.error('Error loading Google Maps API:', error)
			})
	}, [])

	// Fetch autocomplete suggestions
	useEffect(() => {
		if (!value.trim()) {
			setSuggestions([])
			setIsOpen(false)
			return
		}

		// No Places service yet (still loading, or no API key). The custom row
		// still has something to offer, so leave the list's open state alone.
		if (!autocompleteService || !sessionToken) {
			setSuggestions([])
			if (!allowFreeText) setIsOpen(false)
			return
		}

		// Don't fetch if dropdown is already closed
		if (!isOpen) {
			return
		}

		setIsLoading(true)

		const request: google.maps.places.AutocompletionRequest = {
			input: value,
			sessionToken,
		}

		autocompleteService.getPlacePredictions(request, (predictions, status) => {
			setIsLoading(false)

			if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
				setSuggestions([])
				// "Nothing found" is still a list worth showing when the custom
				// row is the answer to it.
				setIsOpen(allowFreeText)
				return
			}

			const converted: PlaceSuggestion[] = predictions.map((prediction) => ({
				placeId: prediction.place_id,
				primaryText: prediction.structured_formatting.main_text,
				secondaryText: prediction.structured_formatting.secondary_text || '',
				fullText: prediction.description,
				types: prediction.types || [],
			}))

			setSuggestions(converted)
			setIsOpen(true)
		})
	}, [value, autocompleteService, sessionToken, isOpen, allowFreeText])

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const insideContainer = containerRef.current?.contains(event.target as Node)
			const insideDropdown = dropdownRef.current?.contains(event.target as Node)
			if (!insideContainer && !insideDropdown) {
				setIsOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange(e.target.value)
		setIsOpen(true)
	}

	// A pick ends the billing session either way, so both paths start the next one.
	const startNewSession = () => {
		if (typeof google !== 'undefined' && google?.maps?.places?.AutocompleteSessionToken) {
			setSessionToken(new google.maps.places.AutocompleteSessionToken())
		}
	}

	const handleSuggestionClick = (suggestion: PlaceSuggestion) => {
		// Update state in the correct order
		setIsOpen(false)
		setSuggestions([])
		onChange(suggestion.fullText)
		onPlaceSelect(suggestion)
		startNewSession()
	}

	/**
	 * Takes the query as typed. The text is handed over untouched — clearing it
	 * or keeping it in the field is the caller's call.
	 */
	const handleCustomLocationClick = () => {
		if (!onFreeTextSelect || query === '') return
		setIsOpen(false)
		setSuggestions([])
		onFreeTextSelect(query)
		startNewSession()
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key !== 'Enter' || !allowFreeText) return
		e.preventDefault()
		handleCustomLocationClick()
	}

	const handleBlur = () => {
		setIsOpen(false)
	}

	const handleClear = () => {
		onChange('')
		setSuggestions([])
		setIsOpen(false)
		inputRef.current?.focus()
	}

	const handleFocus = () => {
		if (suggestions.length > 0 || showCustomRow) {
			setIsOpen(true)
		}
	}

	return (
		<AutocompleteContainer ref={containerRef}>
			<Input
				ref={inputRef}
				id={id}
				aria-label={ariaLabel}
				type="text"
				value={value}
				onChange={handleInputChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				placeholder={placeholder}
				autoComplete="off"
				autoFocus={autoFocus}
			/>
			{value && (
				<ClearButton type="button" onClick={handleClear} aria-label="Clear location">
					×
				</ClearButton>
			)}
			{isOpen &&
				dropdownCoords &&
				(suggestions.length > 0 || showCustomRow || isSearching) &&
				createPortal(
					<AutocompleteDropdown
						ref={dropdownRef}
						$top={dropdownCoords.top}
						$left={dropdownCoords.left}
						$width={dropdownCoords.width}
					>
						{isSearching && <EmptyAutocompleteItem>Searching...</EmptyAutocompleteItem>}
						{showNoMatchesNote && (
							<EmptyAutocompleteItem>No locations found for “{query}”</EmptyAutocompleteItem>
						)}
						{suggestions.map((suggestion) => (
							<AutocompleteItem
								key={suggestion.placeId}
								onMouseDown={(e) => {
									e.preventDefault() // Prevent input blur
									handleSuggestionClick(suggestion)
								}}
							>
								<MainText>{suggestion.primaryText}</MainText>
								{suggestion.secondaryText && (
									<SecondaryText>{suggestion.secondaryText}</SecondaryText>
								)}
							</AutocompleteItem>
						))}
						{showCustomRow && (
							<CustomLocationItem
								onMouseDown={(e) => {
									e.preventDefault() // Prevent input blur
									handleCustomLocationClick()
								}}
							>
								Use “{query}” as a custom location
							</CustomLocationItem>
						)}
					</AutocompleteDropdown>,
					document.body,
				)}
		</AutocompleteContainer>
	)
}

export default LocationAutocomplete
